"use client";

import React, { useMemo } from "react";
import {
  addHours,
  areIntervalsOverlapping,
  differenceInMinutes,
  getHours,
  getMinutes,
  isSameDay,
  startOfDay,
} from "date-fns";

import { cn } from "../../lib/utils";
import { EventItem } from "../event-calendar/event-item";
import { useCurrentTimeIndicator } from "../event-calendar/hooks/use-current-time-indicator";
import {
  WeekCellsHeight,
  EndHour,
  StartHour,
  TimeSlotIntervalMinutes,
} from "../event-calendar/constants";
import { formatTimeString, resolveTimeZone } from "../event-calendar/time-utils";

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const computePositionedEvents = (
  events,
  currentDate,
  businessStartHour
) => {
  const result = [];
  const dayStart = startOfDay(currentDate);

  const sortedEvents = [...events].sort((a, b) => {
    const aStart = new Date(a.start);
    const bStart = new Date(b.start);
    const aEnd = new Date(a.end);
    const bEnd = new Date(b.end);

    if (aStart < bStart) return -1;
    if (aStart > bStart) return 1;

    const aDuration = differenceInMinutes(aEnd, aStart);
    const bDuration = differenceInMinutes(bEnd, bStart);
    return bDuration - aDuration;
  });

  const columns = [];

  sortedEvents.forEach((event) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);

    const adjustedStart = isSameDay(currentDate, eventStart)
      ? eventStart
      : dayStart;
    const adjustedEnd = isSameDay(currentDate, eventEnd)
      ? eventEnd
      : addHours(dayStart, 24);

    const startHour = getHours(adjustedStart) + getMinutes(adjustedStart) / 60;
    const endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60;
    const top = (startHour - businessStartHour) * WeekCellsHeight;
    const height = (endHour - startHour) * WeekCellsHeight;

    let columnIndex = 0;
    let placed = false;

    while (!placed) {
      const col = columns[columnIndex] || [];
      if (col.length === 0) {
        columns[columnIndex] = col;
        placed = true;
      } else {
        const overlaps = col.some((c) =>
          areIntervalsOverlapping(
            { start: adjustedStart, end: adjustedEnd },
            { start: new Date(c.event.start), end: new Date(c.event.end) }
          )
        );
        if (!overlaps) {
          placed = true;
        } else {
          columnIndex++;
        }
      }
    }

    const currentColumn = columns[columnIndex] || [];
    columns[columnIndex] = currentColumn;
    currentColumn.push({ event, end: adjustedEnd });

    const widthFraction = 1 / (columns.length || 1);
    const left = columnIndex * widthFraction;

    result.push({
      event,
      top,
      height,
      left,
      width: widthFraction,
      zIndex: 10 + columnIndex,
    });
  });

  return result;
};

/**
 * @typedef {import("../event-calendar/types").CalendarEvent} CalendarEvent
 */

export function EmployeeDayView({
  currentDate,
  events,
  onEventSelect,
  timezone,
  timeFormat = "24h",
  businessHours,
}) {
  const dayKey = DAY_KEYS[currentDate.getDay()];
  const daySchedule = businessHours?.byDay?.[dayKey];
  const dayEnabled = daySchedule?.enabled !== false;
  const businessStartHour =
    daySchedule?.startHour ?? businessHours?.startHour ?? StartHour;
  const businessEndHour = Math.max(
    daySchedule?.endHour ?? businessHours?.endHour ?? EndHour,
    businessStartHour + 1
  );
  const resolvedTimeZone = resolveTimeZone(timezone);
  const segmentsPerHour = Math.max(
    1,
    Math.round(60 / TimeSlotIntervalMinutes)
  );

  const hours = useMemo(() => {
    const dayStart = startOfDay(currentDate);
    const slots = [];
    const startHourInt = Math.floor(businessStartHour);
    const endHourInt = Math.ceil(businessEndHour);

    for (let hour = startHourInt; hour < endHourInt; hour++) {
      slots.push({
        date: addHours(dayStart, hour),
        hourValue: hour,
      });
    }

    return slots;
  }, [currentDate, businessStartHour, businessEndHour]);

  const dayEvents = useMemo(() => {
    return events
      .filter((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        return (
          isSameDay(currentDate, eventStart) ||
          isSameDay(currentDate, eventEnd) ||
          (currentDate > eventStart && currentDate < eventEnd)
        );
      })
      .sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
  }, [currentDate, events]);

  const timeEvents = useMemo(() => {
    return dayEvents.filter((event) => !event.allDay);
  }, [dayEvents]);

  const positionedEvents = useMemo(
    () =>
      computePositionedEvents(
        timeEvents,
        currentDate,
        businessStartHour
      ),
    [timeEvents, currentDate, businessStartHour]
  );

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    onEventSelect?.(event);
  };

  const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(
    currentDate,
    "day",
    {
      startHour: businessStartHour,
      endHour: businessEndHour,
    }
  );

  return (
    <div data-slot="day-view" className="contents">
      <div className="border-border/70 grid flex-1 grid-cols-[3rem_1fr] overflow-hidden border-t sm:grid-cols-[4rem_1fr]">
        <div>
          {hours.map(({ date: hourDate }, index) => (
            <div
              key={hourDate.toISOString()}
              className="border-border/70 relative h-[var(--week-cells-height)] border-b last:border-b-0"
            >
              {index > 0 && (
                <span className="bg-background text-muted-foreground/70 absolute -top-3 left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs">
                  {formatTimeString(hourDate, resolvedTimeZone, timeFormat, {
                    omitMinutesWhenZero: true,
                  })}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="relative">
          {!dayEnabled && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-xs font-medium text-muted-foreground">
              Clinic closed
            </div>
          )}

          {positionedEvents.map((positionedEvent) => (
            <div
              key={positionedEvent.event.id}
              className="absolute z-10 px-0.5"
              style={{
                top: `${positionedEvent.top}px`,
                height: `${positionedEvent.height}px`,
                left: `${positionedEvent.left * 100}%`,
                width: `${positionedEvent.width * 100}%`,
                zIndex: positionedEvent.zIndex,
              }}
            >
              <div className="h-full w-full">
                <EventItem
                  event={positionedEvent.event}
                  view="day"
                  onClick={(e) => handleEventClick(positionedEvent.event, e)}
                  showTime
                  timezone={resolvedTimeZone}
                  timeFormat={timeFormat}
                />
              </div>
            </div>
          ))}

          {dayEnabled && currentTimeVisible && (
            <div
              className="pointer-events-none absolute right-0 left-0 z-20"
              style={{ top: `${currentTimePosition}%` }}
            >
              <div className="relative flex items-center">
                <div className="bg-primary absolute -left-1 h-2 w-2 rounded-full"></div>
                <div className="bg-primary h-[2px] w-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmployeeDayView;
